/*
|--------------------------------------------------------------------------
| Velora Payment Portal v3.0 — TagadaPay Direct S2S rail
| velora-payment.js RC6  (TAGADA-S2S-008: truthful conditional email copy)
|--------------------------------------------------------------------------
| Replaces the LumniPay card rail ONLY. Preserves unchanged:
| - URL parameter population (order/invoice/reference, amount)
| - Copy buttons, Crypto selector, Wire/Zelle copy
|
| Card flow (PCI boundary):
| 1. @tagadapay/core-js tokenizes card fields IN THE BROWSER
|    (PAN/expiry/CVC go browser -> BasisTheory tokenizer directly).
| 2. Only the single-use tagadaToken + non-sensitive customer/amount
|    fields are POSTed to the velora-pay-tagada Worker.
| 3. Worker: create instrument -> (3DS if SCA) -> process -> normalized result.
| 4. If requireAction=threeds_auth, the challenge is completed in-page,
|    then /status is polled until terminal (succeeded|failed|declined).
| A browser-side result is NEVER trusted: final UI state always comes from
| the Worker's normalized (server-authoritative) response.
|--------------------------------------------------------------------------
*/

document.addEventListener("DOMContentLoaded", () => {
    console.log("VELORA PAYMENT JS v3 (TagadaPay) LOADED");

    const WORKER_URL = "https://velora-pay-checkoutblast.jgoodrick79.workers.dev";
    // CDN modules for @tagadapay/core-js (pin exact version at RC QA).
    // NOTE (TAGADA-S2S-003): the package's "./threeds" subpath export is broken
    // in 2.5.4 (points to a missing dist/threeds/index.js), so ThreedsManager is
    // deep-imported from its concrete module path.
    // RC4 (TAGADA-S2S-006B): SELF-HOSTED vendor bundle — no runtime CDN dependency.
    // velora-tagada-core.v2_5_4.js is a pinned theme asset served from the same
    // theme assets directory as this file. Resolution: page override -> own src -> DOM.
    const VENDOR_FILENAME = "velora-tagada-core.v2_5_4.js";
    const VENDOR_URL = (() => {
        if (window.VELORA_TAGADA_VENDOR_URL) return window.VELORA_TAGADA_VENDOR_URL;
        const self = document.currentScript && document.currentScript.src
            ? document.currentScript.src
            : (document.querySelector('script[src*="velora-payment"]') || {}).src;
        if (self) return self.replace(/velora-payment\.js[^\/]*$/, VENDOR_FILENAME);
        return null;
    })();

    /*=========================================================
      URL PARAMETERS  (unchanged from v2.0)
    =========================================================*/

    const params = new URLSearchParams(window.location.search);

    const order =
        params.get("order") ||
        params.get("invoice") ||
        params.get("reference") ||
        "";

    const amount = params.get("amount") || "";

    const orderDisplay = document.getElementById("orderDisplay");
    const amountDisplay = document.getElementById("amountDisplay");
    const orderInput = document.getElementById("orderInput");
    const cardAmount = document.getElementById("cardAmount");
    const cardReference = document.getElementById("cardReference");

    if (orderDisplay) orderDisplay.textContent = order || "—";
    if (amountDisplay)
        amountDisplay.textContent =
            amount !== "" ? `$${parseFloat(amount).toFixed(2)}` : "—";
    if (orderInput && order) orderInput.value = order;
    if (cardAmount && amount) cardAmount.value = parseFloat(amount).toFixed(2);
    if (cardReference && order) cardReference.value = order;

    /*=========================================================
      CARD PAYMENT — TagadaPay Direct S2S
    =========================================================*/

    const cardForm = document.getElementById("cardPayForm");
    const status = document.getElementById("cardPayStatus");

    let vendorPromise = null;
    function getVendor() {
        if (!vendorPromise) {
            if (!VENDOR_URL) return Promise.reject(new Error("Payment module unavailable."));
            vendorPromise = import(VENDOR_URL);
        }
        return vendorPromise;
    }
    let tokenizerPromise = null;
    function getTokenizer() {
        if (!tokenizerPromise) {
            tokenizerPromise = getVendor().then(async (mod) => {
                const Tokenizer = mod.Tokenizer || mod.CoreJS;
                const t = new Tokenizer({ environment: "production" });
                await t.initialize();
                return { t, mod };
            });
        }
        return tokenizerPromise;
    }
    // Pre-warm the tokenizer as soon as the card panel exists.
    if (cardForm) getTokenizer().catch(() => { /* retried on submit */ });

    function setStatus(msg, color) {
        if (!status) return;
        status.style.color = color || "#00265A";
        status.textContent = msg;
    }

    /*=========================================================
      CARD EXPIRY — normalize visible input to MM/YY
      Narrow patch: no PAN, CVC, amount, routing, or payload changes.
    =========================================================*/

    const expiryInput = cardForm && cardForm.querySelector('[name="cc-exp"]');

    function expiryDigits(value) {
        return String(value || "").replace(/\D/g, "").slice(0, 4);
    }

    function formatExpiry(value) {
        const digits = expiryDigits(value);
        return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
    }

    function parseValidExpiry(value, now = new Date()) {
        const digits = expiryDigits(value);
        if (digits.length !== 4) return { valid: false, reason: "format" };

        const month = Number(digits.slice(0, 2));
        const year = 2000 + Number(digits.slice(2));
        if (month < 1 || month > 12) return { valid: false, reason: "month" };

        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();
        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            return { valid: false, reason: "expired" };
        }

        return {
            valid: true,
            month,
            year,
            value: `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`,
        };
    }

    if (expiryInput) {
        expiryInput.maxLength = 5;
        expiryInput.addEventListener("input", () => {
            const formatted = formatExpiry(expiryInput.value);
            if (expiryInput.value !== formatted) expiryInput.value = formatted;
            expiryInput.setCustomValidity("");
        });
        expiryInput.addEventListener("paste", (event) => {
            event.preventDefault();
            const pasted = event.clipboardData?.getData("text") || "";
            expiryInput.value = formatExpiry(pasted);
            expiryInput.setCustomValidity("");
            expiryInput.dispatchEvent(new Event("input", { bubbles: true }));
        });
    }

    async function postWorker(body) {
        const response = await fetch(WORKER_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.success === false) {
            const e = new Error(result.error || "Payment could not be processed.");
            e.code = result.code;
            e.result = result;
            throw e;
        }
        return result;
    }

    async function pollStatus(paymentId, maxAttempts = 20, delayMs = 1500) {
        for (let i = 0; i < maxAttempts; i++) {
            const r = await fetch(
                `${WORKER_URL}/status?paymentId=${encodeURIComponent(paymentId)}`
            );
            const j = await r.json().catch(() => ({}));
            if (j.terminal) return j;
            await new Promise((res) => setTimeout(res, delayMs));
        }
        throw new Error("Payment verification timed out. Do not retry before contacting billing@velorabiolabs.com.");
    }

    function showOutcome(norm) {
        if (norm.status === "succeeded") {
            setStatus(norm.emailStatus === "accepted"
                ? "✅ Payment successful. A confirmation has been sent to your email."
                : "✅ Payment successful. Your payment has been received.", "#0a7a2f");
            cardForm.querySelectorAll("input,button").forEach((el) => (el.disabled = true));
        } else {
            const detail = norm.declineMessage || norm.declineCode || norm.status || "declined";
            setStatus(`Payment ${norm.status || "failed"}: ${detail}. You have not been charged successfully — please verify with your bank or use another method.`, "#c00");
        }
    }

    if (cardForm) {
        cardForm.addEventListener("submit", async (e) => {
            e.preventDefault();

            const form = new FormData(cardForm);
            const firstName = (form.get("firstName") || "").toString().trim();
            const lastName = (form.get("lastName") || "").toString().trim();
            const email = (form.get("email") || "").toString().trim();
            const phone = (form.get("phone") || "").toString().trim();
            const amountNumber = Number(form.get("amount"));
            const reference = (form.get("reference") || order || "").toString().trim().slice(0, 64);

            // Card fields (READ LOCALLY ONLY — never sent to the Worker)
            const cardNumber = (form.get("cc-number") || "").toString().replace(/\s+/g, "");
            const expiryResult = parseValidExpiry(form.get("cc-exp") || "");
            const expiryDate = expiryResult.valid ? expiryResult.value : "";        // MM/YY (processor contract preserved)
            const cvc = (form.get("cc-cvc") || "").toString().trim();
            const cardholderName =
                (form.get("cc-name") || `${firstName} ${lastName}`).toString().trim();

            if (isNaN(amountNumber)) {
                setStatus("Invalid payment amount.", "#c00");
                return;
            }
            if (amountNumber < 25) {
                setStatus("Card payments must be at least $25.00.", "#c00");
                return;
            }
            if (!/^\d{12,19}$/.test(cardNumber) || !/^\d{3,4}$/.test(cvc)) {
                setStatus("Please check the card number, expiry (MM/YY) and CVC.", "#c00");
                return;
            }
            if (!expiryResult.valid) {
                const expiryMessage = expiryResult.reason === "expired"
                    ? "The card expiry date has passed."
                    : expiryResult.reason === "month"
                        ? "Please enter a valid expiry month from 01 to 12."
                        : "Please enter the card expiry as MM/YY.";
                if (expiryInput) expiryInput.setCustomValidity(expiryMessage);
                setStatus(expiryMessage, "#c00");
                return;
            }
            if (expiryInput) {
                expiryInput.value = expiryDate;
                expiryInput.setCustomValidity("");
            }
            const amountCents = Math.round(amountNumber * 100);

            try {
                /* 1 ── tokenize IN BROWSER (PCI boundary) */
                setStatus("Securing card details…");
                const { t } = await getTokenizer();
                const raw = await t.tokenizeCardRaw({
                    cardNumber,
                    expiryDate,
                    cvc,
                    cardholderName,
                });
                const tagadaToken =
                    raw.tagadaToken || (await t.tokenizeCard({ cardNumber, expiryDate, cvc, cardholderName }));
                const scaRequired = raw?.metadata?.auth?.scaRequired === true;

                /* 2 ── non-SCA fast path (typical US cards) */
                if (!scaRequired) {
                    setStatus("Processing payment…");
                    const result = await postWorker({
                        step: "pay",
                        tagadaToken,
                        firstName, lastName, email,
                        phone: phone || undefined,
                        amountCents,
                        reference: reference || undefined,
                    });
                    if (result.requireAction === "threeds_auth" && result.threeds) {
                        await runThreedsChallenge(result);
                        return;
                    }
                    showOutcome(result);
                    return;
                }

                /* 3 ── SCA path: instrument -> local 3DS session -> persist -> process */
                setStatus("Additional authentication required — preparing secure session…");
                const inst = await postWorker({
                    step: "instrument",
                    tagadaToken,
                    firstName, lastName, email,
                    phone: phone || undefined,
                });

                let threedsSessionId;
                try {
                    // Pinned contract (TAGADA-S2S-003): ThreedsManager.createSession()
                    const threeds = await getThreedsManager();
                    const localSession = await threeds.createSession(
                        {
                            id: inst.paymentInstrumentId,
                            token: raw.token,
                            type: inst.instrumentType || "card",
                            card: {
                                expirationMonth: inst.card?.expMonth,
                                expirationYear: inst.card?.expYear,
                                last4: inst.card?.last4,
                                bin: raw.bin,
                            },
                        },
                        {
                            amount: amountCents,
                            currency: "USD",
                            customerInfo: { name: `${firstName} ${lastName}`, email },
                        }
                    );
                    const persisted = await postWorker({
                        step: "threeds",
                        paymentInstrumentId: inst.paymentInstrumentId,
                        provider: localSession.provider || "basis_theory",
                        sessionData: (localSession.metadata && localSession.metadata.raw) || localSession,
                    });
                    threedsSessionId = persisted.threedsSessionId;
                } catch (scaErr) {
                    console.warn("3DS pre-session unavailable; processing without pre-created session.", scaErr);
                }

                setStatus("Processing payment…");
                const result = await postWorker({
                    step: "process",
                    paymentInstrumentId: inst.paymentInstrumentId,
                    customerId: inst.customerId,
                    amountCents,
                    threedsSessionId,
                    reference: reference || undefined,
                    email,
                });
                if (result.requireAction === "threeds_auth" && result.threeds) {
                    await runThreedsChallenge(result);
                    return;
                }
                showOutcome(result);
            } catch (err) {
                console.error(err);
                setStatus(err.message || "Unable to connect to payment server.", "#c00");
            }
        });
    }

    let threedsManagerPromise = null;
    function getThreedsManager() {
        // Pinned contract (TAGADA-S2S-003, core-js 2.5.4 type defs + useThreeds source):
        //   new ThreedsManager({ defaultProvider:'basis_theory', providers:{ basis_theory:{ apiKey } } })
        //   await manager.initializeProvider('basis_theory', apiKey)
        // apiKey = SDK-embedded PUBLIC BasisTheory client key via getBasisTheoryApiKey(env).
        if (!threedsManagerPromise) {
            threedsManagerPromise = getVendor().then(async (mod) => {
                const apiKey = mod.getBasisTheoryApiKey("production");
                const manager = new mod.ThreedsManager({
                    defaultProvider: "basis_theory",
                    providers: { basis_theory: { apiKey } },
                });
                await manager.initializeProvider("basis_theory", apiKey);
                return manager;
            });
        }
        return threedsManagerPromise;
    }

    async function runThreedsChallenge(result) {
        try {
            setStatus("🔐 Bank authentication required — follow the prompt…");
            const threeds = await getThreedsManager();
            // startChallenge(challenge: ThreedsChallenge, options?) -> ChallengeCompletion
            // Field mapping (matches the official example exactly):
            //   sessionId        <- threedsSession.externalSessionId
            //   acsChallengeUrl  <- threedsSession.acsChallengeUrl
            //   acsTransactionId <- threedsSession.acsTransID
            //   threeDSVersion   <- threedsSession.messageVersion
            const completion = await threeds.startChallenge({
                sessionId: result.threeds.externalSessionId,
                acsChallengeUrl: result.threeds.acsChallengeUrl,
                acsTransactionId: result.threeds.acsTransID,
                threeDSVersion: result.threeds.messageVersion,
            }, { mode: "modal" });
            if (completion && completion.success === false) {
                console.warn("3DS challenge completion:", completion.status, completion.error);
            }
            setStatus("Verifying payment…");
            const finalState = await pollStatus(result.paymentId);
            showOutcome(finalState);
        } catch (e) {
            console.error(e);
            setStatus("Verifying payment…");
            try {
                const finalState = await pollStatus(result.paymentId, 8);
                showOutcome(finalState);
            } catch {
                setStatus(
                    "This card requires additional bank authentication that could not be completed. You have not been charged — please use another card or contact billing@velorabiolabs.com.",
                    "#c00"
                );
            }
        }
    }

    /*=========================================================
      COPY BUTTONS  (unchanged from v2.0)
    =========================================================*/

    document.querySelectorAll(".copy-btn").forEach(button => {
        button.addEventListener("click", async () => {
            if (button.textContent.trim().toLowerCase() === "copy all") {
                const card = button.closest(".pay-card");
                const values = [];
                card.querySelectorAll(".copy-target").forEach(el => {
                    values.push(el.textContent.trim());
                });
                try {
                    await navigator.clipboard.writeText(values.join("\n"));
                    button.textContent = "Copied!";
                    setTimeout(() => { button.textContent = "Copy All"; }, 1500);
                } catch (e) {
                    alert("Clipboard unavailable.");
                }
                return;
            }
            const target = button.parentElement.querySelector(".copy-target");
            if (!target) return;
            try {
                await navigator.clipboard.writeText(target.textContent.trim());
                const original = button.textContent;
                button.textContent = "Copied!";
                setTimeout(() => { button.textContent = original; }, 1200);
            } catch {
                alert("Clipboard unavailable.");
            }
        });
    });

    /*=========================================================
      CRYPTO CARDS  (unchanged from v2.0)
    =========================================================*/

    document.querySelectorAll(".crypto-item").forEach(card => {
        card.addEventListener("click", async () => {
            const values = [];
            card.querySelectorAll(".crypto-address").forEach(el => {
                values.push(el.textContent.trim());
            });
            try {
                await navigator.clipboard.writeText(values.join("\n"));
                const status2 = card.querySelector(".copy-status");
                if (status2) {
                    const original = status2.textContent;
                    status2.textContent = "Copied!";
                    status2.classList.add("success");
                    setTimeout(() => {
                        status2.textContent = original;
                        status2.classList.remove("success");
                    }, 1500);
                }
            } catch {
                alert("Clipboard unavailable.");
            }
        });
    });

    /*=========================================================
      CRYPTO NETWORK SELECTOR  (unchanged from v2.0)
    =========================================================*/

    const networkSelect = document.getElementById("networkSelect");
    const networkWarning = document.getElementById("networkWarning");

    if (networkSelect) {
        networkSelect.addEventListener("change", () => {
            const value = networkSelect.value;
            document.querySelectorAll(".crypto-item").forEach(card => {
                card.style.display = "none";
            });
            if (!value) {
                document.querySelectorAll(".crypto-item").forEach(card => {
                    card.style.display = "";
                });
                networkWarning.textContent = "";
                return;
            }
            document.querySelectorAll(`.crypto-item[data-network="${value}"]`).forEach(card => {
                card.style.display = "";
            });
            if (value === "ERC20")
                networkWarning.textContent = "Ethereum network fees may apply.";
            else if (value === "XRP")
                networkWarning.textContent = "Both XRP Address AND Destination Tag are required.";
            else
                networkWarning.textContent = "";
        });
    }
});
